import type { NotificationRecord } from '@/shared/types/notification.types';

/**
 * Translate a Notification's deepLink / type into a concrete web-admin URL.
 *
 * The server delivers deepLinks like
 *   { screen: 'WhatsappChat', params: { conversationId: '...' } }
 * which we map to `/communication/consumer-whatsapp?conversationId=...` here
 * (and the Facebook / Instagram screens to their own inboxes).
 *
 * For server-shaped types (e.g. 'lead.assigned') we fall back to the typed
 * mapping below. If nothing matches, return null and the caller leaves the
 * user on the bell popover.
 */
export function resolveNotificationRoute(n: NotificationRecord): string | null {
  const screen = n.deepLink?.screen;
  const params = n.deepLink?.params ?? {};

  if (screen) {
    switch (screen) {
      case 'WhatsappChat':
      case 'Whatsapp':
      case 'ConsumerWhatsapp': {
        const conversationId = params.conversationId as string | undefined;
        if (conversationId)
          return `/communication/consumer-whatsapp?conversationId=${encodeURIComponent(
            conversationId
          )}`;
        return '/communication/consumer-whatsapp';
      }
      case 'FacebookChat':
      case 'ConsumerFacebook': {
        const conversationId = params.conversationId as string | undefined;
        if (conversationId)
          return `/communication/consumer-facebook?conversationId=${encodeURIComponent(
            conversationId
          )}`;
        return '/communication/consumer-facebook';
      }
      case 'InstagramChat':
      case 'ConsumerInstagram': {
        const conversationId = params.conversationId as string | undefined;
        if (conversationId)
          return `/communication/consumer-instagram?conversationId=${encodeURIComponent(
            conversationId
          )}`;
        return '/communication/consumer-instagram';
      }
      case 'LeadDetails':
      case 'Lead': {
        const id = (params.id as string) ?? (params.leadId as string) ?? undefined;
        return id ? `/leads/details/${id}` : '/leads/list';
      }
      case 'FinanceApplicationDetails':
      case 'FinanceApplication': {
        const id = (params.id as string) ?? (params.applicationId as string) ?? undefined;
        return id ? `/finance-applications/details/${id}` : '/finance-applications/list';
      }
      case 'CustomerDetails':
      case 'Customer': {
        const id = (params.id as string) ?? (params.customerId as string) ?? undefined;
        return id ? `/customers/details/${id}` : '/customers/list';
      }
      case 'Helpdesk':
      case 'HelpdeskTicket': {
        const id = (params.id as string) ?? (params.ticketId as string) ?? undefined;
        return id ? `/helpdesk/detail/${id}` : '/helpdesk';
      }
      case 'MyFiles':
      case '/my-files':
      case 'my-files/list': {
        const jobId = params.jobId as string | undefined;
        return jobId
          ? `/my-files/list?jobId=${encodeURIComponent(jobId)}`
          : '/my-files/list';
      }
      default:
        break;
    }
  }

  // Server-typed fallbacks
  const type = (n.type || '').toLowerCase();
  if (type.startsWith('whatsapp')) {
    const conversationId = (n.data?.conversationId as string) ?? undefined;
    if (conversationId)
      return `/communication/consumer-whatsapp?conversationId=${encodeURIComponent(
        conversationId
      )}`;
    return '/communication/consumer-whatsapp';
  }
  if (type.startsWith('lead')) {
    const id = (n.data?.leadId as string) ?? (n.data?.id as string) ?? undefined;
    return id ? `/leads/details/${id}` : '/leads/list';
  }
  if (type.includes('finance')) {
    const id = (n.data?.applicationId as string) ?? (n.data?.id as string) ?? undefined;
    return id ? `/finance-applications/details/${id}` : '/finance-applications/list';
  }
  if (type.startsWith('customer')) {
    const id = (n.data?.customerId as string) ?? (n.data?.id as string) ?? undefined;
    return id ? `/customers/details/${id}` : '/customers/list';
  }
  if (n.data?.module === 'export' || n.templateId === 'exportReady') {
    const jobId = n.data?.jobId as string | undefined;
    return jobId
      ? `/my-files/list?jobId=${encodeURIComponent(jobId)}`
      : '/my-files/list';
  }
  return null;
}
